import React, { useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ClipCategory, ClipLabel, ClipLabelDimension, ClipLabelEdit, ClipLabelVocabulary,
} from 'services/VideoEditingService';
import { humanize } from '../format';
import CategoryPicker from './CategoryPicker';

/**
 * The editable fields of a clip label, as a CONTROLLED form: the parent owns the draft.
 *
 * Extracted from ClipLabelEditor so two surfaces can share one set of fields — the Video
 * Editing labels panel (which keeps its own read-only-then-pencil flow) and the B-Roll
 * Library's Edit modal, which mounts these alongside a trimmer and saves both with one
 * button. A second copy of these fields would drift: the product→SKU invalidation and the
 * "don't send categories the surface never showed" rule are easy to get subtly wrong.
 */

// The form's working copy of one label. Plain values, so a cancel just discards it.
// Deliberately no `clipType` (the model's Type classification is no longer offered) and no
// in/out — the range is the trimmer's, not a field.
export interface Draft {
  name: string;
  productCategory: string | null;
  productHandle: string | null;
  productConfidence: string;
  clipSubject: string[];
  categories: string[];
  keywords: string[];
  description: string;
  searchNotes: string;
}

// A blank one, for a clip being filed for the first time. Exported so a surface creating a
// label starts from the same shape `draftFrom` produces — a hand-rolled literal drifts the
// moment a field is added here.
export const EMPTY_CLIP_DRAFT: Draft = {
  name: '',
  productCategory: null,
  productHandle: null,
  productConfidence: 'none',
  clipSubject: [],
  categories: [],
  keywords: [],
  description: '',
  searchNotes: '',
};

export function draftFrom(label: ClipLabel): Draft {
  return {
    name: label.name || '',
    productCategory: label.productCategory,
    productHandle: label.productHandle,
    productConfidence: label.productConfidence || 'none',
    clipSubject: label.clipSubject || [],
    categories: label.categories || [],
    keywords: label.keywords || [],
    description: label.description || '',
    searchNotes: label.searchNotes || '',
  };
}

/**
 * Turn a draft into the request body. One place, because two rules live here that are
 * invisible at the call site:
 *
 *  - `clipType` is never sent. The form doesn't offer it, and an omitted key is left alone by
 *    the API — so an edit here can't blank out a value the b-roll talking rule still reads.
 *  - `categories` is sent only when the surface actually offered the field. An empty array from
 *    a page that never loaded the vocabulary would CLEAR the clip's categories.
 */
export function clipLabelEditFrom(
  draft: Draft,
  { categoriesOffered }: { categoriesOffered: boolean },
): ClipLabelEdit {
  return {
    name: draft.name,
    productCategory: draft.productCategory,
    productHandle: draft.productHandle,
    productConfidence: draft.productConfidence,
    clipSubject: draft.clipSubject,
    ...(categoriesOffered ? { categories: draft.categories } : {}),
    keywords: draft.keywords,
    description: draft.description,
    searchNotes: draft.searchNotes,
  };
}

interface ClipLabelFieldsProps {
  draft: Draft;
  onDraftChange: (next: Draft) => void;
  vocabulary: ClipLabelVocabulary | null;
  // Omitted (or null) hides the category field entirely, so a surface that hasn't loaded the
  // vocabulary doesn't render an empty picker. Pairs with `categoriesOffered` above.
  categories?: ClipCategory[] | null;
  onCreateCategory?: (name: string) => Promise<ClipCategory>;
  // The name field is the first thing a person fills in when creating a clip, but not when
  // correcting an existing one.
  autoFocusName?: boolean;
  // Whether to offer the model's product-confidence scale. False once a human has saved the
  // label — the parent knows that (labelSource), this controlled component doesn't.
  showConfidence?: boolean;
  // Fold everything past the product fields behind a "More details" toggle. For surfaces
  // where filing the clip is a detour rather than the job — the same fields, just not all
  // at once.
  detailsCollapsible?: boolean;
}

export default function ClipLabelFields({
  draft,
  onDraftChange,
  vocabulary,
  categories,
  onCreateCategory,
  autoFocusName,
  showConfidence = true,
  detailsCollapsible = false,
}: ClipLabelFieldsProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const dimensions = useMemo(() => vocabulary?.dimensions || [], [vocabulary]);
  const products = useMemo(() => vocabulary?.products || [], [vocabulary]);
  const dimensionByKey = useMemo(() => {
    const map = new Map<string, ClipLabelDimension>();
    for (const d of dimensions) map.set(d.key, d);
    return map;
  }, [dimensions]);

  const valuesFor = (key: string) => (dimensionByKey.get(key)?.values || []).map(v => v.value);
  const set = (patch: Partial<Draft>) => onDraftChange({ ...draft, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* First, because it's what a person reads on the card and the only field that
          is purely theirs — the model never writes it. */}
      <TextField
        size="small"
        label="Name"
        autoFocus={autoFocusName}
        placeholder="e.g. hands squeezing the bolster"
        helperText="What you'd call this clip when looking for it later."
        value={draft.name}
        onChange={e => set({ name: e.target.value })}
      />

      {categories && (
        <CategoryPicker
          categories={categories}
          value={draft.categories}
          onChange={slugs => set({ categories: slugs })}
          onCreate={onCreateCategory}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Autocomplete
          size="small"
          sx={{ flexGrow: 1, minWidth: 220 }}
          options={valuesFor('productCategory')}
          getOptionLabel={humanize}
          value={draft.productCategory}
          onChange={(_, value) => set({
            productCategory: value,
            // Changing the category invalidates any SKU pinned under the old one.
            productHandle: value && value !== 'none' && draft.productHandle
              && products.find(p => p.handle === draft.productHandle)?.productCategory === value
              ? draft.productHandle
              : null,
            productConfidence: value && value !== 'none'
              ? (draft.productConfidence === 'none' ? 'medium' : draft.productConfidence)
              : 'none',
          })}
          renderInput={params => (
            <TextField {...params} label={dimensionByKey.get('productCategory')?.label || 'Product'} />
          )}
        />
        {/* The MODEL's confidence in its product guess. Hidden once a person has saved the
            label: at that point the interesting fact is that a human vetted it, not how sure the
            labeler had been, and offering the scale invites someone to answer a question about
            the model's state of mind. */}
        {showConfidence && (
          <Autocomplete
            size="small"
            sx={{ width: 150 }}
            disableClearable
            options={vocabulary?.productConfidence || []}
            value={draft.productConfidence}
            onChange={(_, value) => set({ productConfidence: value || 'none' })}
            renderInput={params => <TextField {...params} label="Confidence" />}
          />
        )}
      </Box>

      {/* Everything below is worth having and rarely the reason someone opened the form, so a
          surface where filing the clip is the JOB (the library's own editor) shows it outright
          and one where filing is a detour (adding from the video editor) folds it away. Same
          fields either way — a shorter form that collects less is a library with less in it. */}
      {detailsCollapsible && (
        <Button
          size="small"
          variant="text"
          startIcon={detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          onClick={() => setDetailsOpen(open => !open)}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', ml: -0.5 }}
        >
          {detailsOpen ? 'Fewer details' : 'More details'}
        </Button>
      )}
      {/* Unmounted while closed, not merely hidden: the draft lives in the parent, so there
          is nothing to lose, and a form that still answers to a screen reader after it has
          been folded away is a form with hidden fields in it. */}
      <Collapse in={!detailsCollapsible || detailsOpen} unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Optional exact SKU, narrowed to the chosen category so the list stays
          short and can't contradict the category above. Hidden until a category
          is picked: with none chosen there is nothing to narrow to, and the
          field would just offer "No options". */}
      {draft.productCategory && draft.productCategory !== 'none' && (
        <Autocomplete
          size="small"
          options={products
            .filter(p => p.productCategory === draft.productCategory)
            .map(p => p.handle)}
          getOptionLabel={h => products.find(p => p.handle === h)?.name || h}
          value={draft.productHandle}
          onChange={(_, value) => set({ productHandle: value })}
          renderInput={params => (
            <TextField
              {...params}
              label="Specific product (optional)"
              placeholder="Leave blank unless the exact listing is obvious"
              helperText="Only pin this when branding or packaging makes the exact listing unmistakable."
            />
          )}
        />
      )}

      {/* One field. Subject and Action used to be two, with overlapping vocabularies that made
          the same footage tagged inconsistently — see the taxonomy for what folded into what. */}
      <Autocomplete
        size="small"
        multiple
        options={valuesFor('clipSubject')}
        getOptionLabel={humanize}
        value={draft.clipSubject}
        onChange={(_, value) => set({ clipSubject: value })}
        renderInput={params => (
          <TextField
            {...params}
            label={dimensionByKey.get('clipSubject')?.label || 'Shows / does'}
            helperText={dimensionByKey.get('clipSubject')?.question}
          />
        )}
      />

      <Autocomplete
        size="small"
        multiple
        freeSolo
        options={[]}
        value={draft.keywords}
        onChange={(_, value) => set({ keywords: (value as string[]).map(v => v.trim()).filter(Boolean) })}
        renderInput={params => (
          <TextField {...params} label="Keywords" helperText="Anything an editor might search for. Press Enter after each." />
        )}
      />

      <TextField
        size="small"
        label="Describes"
        value={draft.description}
        onChange={e => set({ description: e.target.value })}
        multiline
        minRows={2}
      />
      <TextField
        size="small"
        label="Notes"
        helperText="Free text — anything else worth being findable by."
        value={draft.searchNotes}
        onChange={e => set({ searchNotes: e.target.value })}
        multiline
        minRows={2}
      />
        </Box>
      </Collapse>
    </Box>
  );
}
